import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { PrivacyPolicyPage, TermsOfServicePage, RefundPolicyPage } from "./LegalPages";
import EmbeddedSignup from "./embsigup";

const chatMessages = [
  { type: "out", text: "Hi", delay: 0 },
  { type: "in", text: "👋 Welcome to Serenity Wellness!\n\nWhat would you like to book?", delay: 800 },
  { type: "buttons", items: ["🧘 Yoga Class", "💆 Massage", "🧠 Meditation"], delay: 1400 },
  { type: "out", text: "🧘 Yoga Class", delay: 2200 },
  { type: "in", text: "Perfect! Choose a slot:", delay: 3000 },
  { type: "buttons", items: ["7:00 AM", "9:00 AM", "6:00 PM"], delay: 3500 },
  { type: "out", text: "9:00 AM", delay: 4300 },
  { type: "in", text: "✅ Booking Confirmed!\n\n📅 Tomorrow, 9:00 AM\n🧘 Yoga Class\n\nSee you soon! 🙏", delay: 5000 },
];

const features = [
  { icon: "smart_toy", title: "Intelligent WhatsApp Bot", desc: "Customers book 24/7 through natural WhatsApp chat — no app downloads, no forms, no friction." },
  { icon: "calendar_month", title: "Smart Scheduling", desc: "Real-time availability, automatic slot management, and buffer time handled automatically." },
  { icon: "notifications_active", title: "Automated Reminders", desc: "Customers get reminders before appointments. Admins get daily summaries every morning at 8 AM." },
  { icon: "payments", title: "Payment Collection", desc: "Collect advance payments via Razorpay to reduce no-shows and secure your schedule." },
  { icon: "dashboard", title: "Admin Dashboard", desc: "See all bookings, revenue, and customer history in one clean dashboard." },
  { icon: "hub", title: "Multi-Business Ready", desc: "Each business gets its own bot and branding, powered by one platform behind the scenes." },
];

const faqs = [
  { q: "Do my customers need to download anything?", a: "Absolutely not. They just message your WhatsApp Business number — the bot handles everything automatically within the chat." },
  { q: "Will customers see 'Bookwello' branding?", a: "Never. Customers only see your business name and number. Bookwello is completely invisible — we just power the automation behind the scenes." },
  { q: "What if I already have a WordPress website?", a: "No problem. Bookwello works independently. We connect to your WhatsApp number — no website changes needed." },
  { q: "How long does it take to go live?", a: "Most businesses are live in 2–3 days. We handle the entire setup so you can focus on running your business." },
  { q: "What kinds of businesses can use Bookwello?", a: "Any appointment-based business: wellness centres, yoga studios, spas, salons, dental clinics, coaching, gyms, and more." },
];

/* ─── Nav ─── */
function Nav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const close = () => setMenuOpen(false);

  return (
    <header className={`bg-slate-50/80 glass-nav sticky top-0 z-50 transition-all ${scrolled ? "shadow-sm" : ""}`}>
      <nav className="flex justify-between items-center w-full px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-8">
          <Link to="/" className="text-2xl font-bold tracking-tight text-slate-900 font-headline" onClick={close}>
            <img src="/logo-main.png" style={{ "height": "30px" }} alt="Logo" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors font-label text-xs">Features</Link>
            <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors font-label text-xs">Pricing</Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors font-label text-xs">Contact</Link>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <button className="text-slate-600 hover:text-slate-900 font-semibold text-sm px-4 py-2.5 rounded-xl border border-outline-variant/30 hover:border-secondary/30 transition-all" id="login-btn">
            Log In
          </button>
          <button onClick={() => navigate("/onboarding")} className="bg-secondary text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 transition-all duration-200" id="signup-btn">
            Sign Up
          </button>
        </div>

        {/* Hamburger */}
        <button className="flex md:hidden flex-col gap-[5px] p-1 z-[110]" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <span className={`block w-[22px] h-[2px] bg-slate-900 rounded transition-all ${menuOpen ? "rotate-45 translate-y-[7px]" : ""}`} />
          <span className={`block w-[22px] h-[2px] bg-slate-900 rounded transition-all ${menuOpen ? "opacity-0" : ""}`} />
          <span className={`block w-[22px] h-[2px] bg-slate-900 rounded transition-all ${menuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`} />
        </button>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="fixed inset-0 bg-white/98 z-[99] flex flex-col items-center justify-center gap-6">
            <Link to="/" onClick={close} className="text-lg font-semibold text-on-surface">Features</Link>
            <Link to="/" onClick={close} className="text-lg font-semibold text-on-surface">Pricing</Link>
            <Link to="/contact" onClick={close} className="text-lg font-semibold text-on-surface">Contact</Link>
            <button onClick={close} className="text-slate-600 font-semibold text-sm px-6 py-3 rounded-xl border border-outline-variant/30 w-48 text-center">Log In</button>
            <button onClick={() => { close(); navigate("/onboarding"); }} className="bg-secondary text-white px-6 py-3 rounded-xl font-semibold text-sm w-48 text-center">Sign Up</button>
          </div>
        )}
      </nav>
    </header>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="w-full border-t border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto py-12 px-6 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex flex-col items-center md:items-start gap-2">
          <Link to="/" className="font-headline font-bold text-slate-900 text-xl"><img src="/logo-main.png" style={{ "height": "30px" }} alt="Logo" /></Link>
          <p className="font-body text-xs text-slate-500">© 2026 Bookwello. WhatsApp Booking Automation.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-8">
          <Link to="/privacy-policy" className="text-slate-500 hover:text-slate-800 underline decoration-emerald-500/30 font-body text-xs transition-all">Privacy</Link>
          <Link to="/terms-of-service" className="text-slate-500 hover:text-slate-800 underline decoration-emerald-500/30 font-body text-xs transition-all">Terms</Link>
          <Link to="/refund-policy" className="text-slate-500 hover:text-slate-800 underline decoration-emerald-500/30 font-body text-xs transition-all">Refund Policy</Link>
          <Link to="/contact" className="text-slate-500 hover:text-slate-800 underline decoration-emerald-500/30 font-body text-xs transition-all">Contact</Link>
        </div>
        <div className="flex gap-4">
          <a href="mailto:admin@bookwello.in" className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-secondary-container hover:text-secondary transition-colors">
            <span className="material-symbols-outlined text-sm">mail</span>
          </a>
          <a href="https://wa.me/8824994138" className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-secondary-container hover:text-secondary transition-colors">
            <span className="material-symbols-outlined text-sm">chat</span>
          </a>
        </div>
      </div>
    </footer>
  );
}

/* ─── Home Page ─── */
function HomePage() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState([]);
  const [faq, setFaq] = useState(null);

  const runChat = () => {
    setVisible([]);
    const timers = chatMessages.map((m, i) => setTimeout(() => setVisible(p => [...p, i]), m.delay));
    return timers;
  };
  useEffect(() => { const t = runChat(); return () => t.forEach(clearTimeout); }, []);
  useEffect(() => {
    if (visible.length === chatMessages.length) {
      const t = setTimeout(() => runChat(), 3000);
      return () => clearTimeout(t);
    }
  }, [visible]);

  return (<>
    {/* Hero */}
    <section className="relative pt-20 pb-32 overflow-hidden bg-surface">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="z-10">
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label fade-up">
            WhatsApp Booking Automation
          </span>
          <h1 className="text-5xl lg:text-7xl font-headline font-extrabold text-on-primary-fixed leading-tight mb-8 fade-up fade-up-1">
            Let your business book while you <span className="text-secondary">sleep.</span>
          </h1>
          <p className="text-xl text-on-surface-variant leading-relaxed mb-10 max-w-xl fade-up fade-up-2">
            Bookwello turns your WhatsApp into a 24/7 booking assistant. Customers book through a simple chat — no app, no form, no friction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 fade-up fade-up-3">
            <button onClick={() => navigate("/contact")} className="bg-primary-container text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-black transition-all flex items-center justify-center gap-2 group">
              Start Free Trial
              <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </button>
            <a href="#how" className="bg-surface-container-lowest text-on-surface px-8 py-4 rounded-xl font-bold text-lg shadow-sm hover:bg-surface-container-low transition-all text-center">
              See how it works
            </a>
          </div>
          <div className="flex gap-10 mt-12 fade-up fade-up-4">
            {[["3min", "Avg booking time"], ["60%", "Fewer no-shows"], ["24/7", "Always available"]].map(([n, l]) => (
              <div key={l}>
                <div className="text-2xl font-headline font-extrabold text-on-primary-fixed">{n}</div>
                <div className="text-xs text-on-surface-variant mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* WhatsApp Mockup */}
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-tr from-secondary-container to-transparent opacity-20 rounded-[3rem] blur-3xl" />
          <div className="relative bg-white rounded-[2.5rem] p-4 shadow-2xl border-[8px] border-primary-container overflow-hidden max-w-[400px] mx-auto">
            <div className="flex items-center justify-between px-6 py-4 bg-[#075e54] text-white -mt-4 -mx-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-surface-container-highest flex items-center justify-center text-primary-container">
                  <span className="material-symbols-outlined">smart_toy</span>
                </div>
                <div>
                  <p className="font-bold text-sm">Serenity Wellness</p>
                  <p className="text-[10px] opacity-80">● Online</p>
                </div>
              </div>
            </div>
            <div className="space-y-3 py-2 min-h-[380px] flex flex-col">
              {chatMessages.map((m, i) => {
                if (!visible.includes(i)) return null;
                if (m.type === "out") return (
                  <div key={i} className="whatsapp-bubble-sent p-3 text-xs text-on-surface self-end max-w-[80%] shadow-sm animate-[msgAppear_0.4s_ease_forwards] opacity-0 translate-y-2">
                    {m.text}
                    <p className="text-[9px] text-right mt-1 text-on-surface-variant">✓✓</p>
                  </div>
                );
                if (m.type === "in") return (
                  <div key={i} className="whatsapp-bubble-received p-3 text-xs text-on-surface self-start max-w-[80%] shadow-sm whitespace-pre-line animate-[msgAppear_0.4s_ease_forwards] opacity-0 translate-y-2">
                    {m.text}
                  </div>
                );
                if (m.type === "buttons") return (
                  <div key={i} className="flex flex-col gap-2 self-start max-w-[80%]">
                    {m.items.map((item, j) => (
                      <div key={j} className="whatsapp-bubble-sent p-2.5 text-xs text-center text-[#075e54] font-semibold border border-[#075e54]/20 rounded-lg shadow-sm animate-[msgAppear_0.4s_ease_forwards] opacity-0 translate-y-2" style={{ animationDelay: `${j * 100}ms` }}>
                        {item}
                      </div>
                    ))}
                  </div>
                );
                return null;
              })}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Social Proof */}
    <section className="bg-surface-container-low py-16">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <p className="text-sm font-bold text-on-surface-variant mb-10 font-label uppercase tracking-widest">Built for every appointment-based business</p>
        <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-24">
          {["Wellness Centres", "Yoga Studios", "Spas & Salons", "Dental Clinics", "Coaching", "Gyms"].map(b => (
            <span key={b} className="font-headline font-bold text-lg text-on-surface-variant/40 hover:text-on-surface-variant transition-colors">{b}</span>
          ))}
        </div>
      </div>
    </section>

    {/* How It Works */}
    <section id="how" className="py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">How It Works</span>
          <h2 className="text-4xl font-headline font-bold text-primary-container mb-4">Three steps to automated bookings</h2>
          <p className="text-on-surface-variant max-w-2xl mx-auto">Get your business running on autopilot in days, not months.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { n: "01", icon: "link", t: "Connect your WhatsApp", d: "We register your existing WhatsApp Business number with our platform. Your number, your brand — we just power it." },
            { n: "02", icon: "tune", t: "We configure your bot", d: "Tell us your services, pricing, and availability. We build a custom booking flow that matches your business." },
            { n: "03", icon: "rocket_launch", t: "Customers book instantly", d: "Customers message you, pick a service and time, get instant confirmation. You get notified immediately." },
          ].map((s, i) => (
            <div key={s.n} className={`p-8 rounded-[2rem] flex flex-col group transition-shadow ${i === 1 ? "bg-primary-container text-white shadow-2xl md:scale-105" : "bg-surface-container-lowest border border-outline-variant/15 hover:shadow-xl"}`}>
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${i === 1 ? "bg-secondary" : "bg-secondary-container/50 text-secondary"}`}>
                <span className="material-symbols-outlined text-3xl">{s.icon}</span>
              </div>
              <div className={`text-5xl font-headline font-extrabold mb-4 ${i === 1 ? "text-white/20" : "text-secondary/20"}`}>{s.n}</div>
              <h3 className={`text-2xl font-headline font-bold mb-4 ${i === 1 ? "" : "group-hover:text-secondary transition-colors"}`}>{s.t}</h3>
              <p className={i === 1 ? "text-on-primary-container leading-relaxed" : "text-on-surface-variant leading-relaxed"}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Features */}
    <section id="features" className="py-32 bg-surface-container-low">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">Features</span>
          <h2 className="text-4xl font-headline font-bold text-primary-container mb-4">Everything your business needs</h2>
          <p className="text-on-surface-variant max-w-2xl mx-auto">A complete booking automation system that grows with you.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map(f => (
            <div key={f.title} className="bg-surface-container-lowest p-8 rounded-[2rem] border border-outline-variant/15 hover:shadow-xl transition-shadow group">
              <div className="w-14 h-14 bg-secondary-container/50 rounded-2xl flex items-center justify-center mb-6 text-secondary">
                <span className="material-symbols-outlined text-3xl">{f.icon}</span>
              </div>
              <h3 className="text-xl font-headline font-bold mb-3 group-hover:text-secondary transition-colors">{f.title}</h3>
              <p className="text-on-surface-variant leading-relaxed text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Pricing */}
    <section id="pricing" className="py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">Pricing</span>
          <h2 className="text-4xl font-headline font-bold text-primary-container mb-4">Simple, honest pricing</h2>
          <p className="text-on-surface-variant max-w-2xl mx-auto">No setup fees. No hidden charges. Cancel anytime.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {[
            { p: "Starter", a: "1,999", d: "Perfect for solo practitioners", f: ["WhatsApp booking bot", "Up to 100 bookings/month", "Admin notifications", "1 service category", "Email support"] },
            { p: "Standard", a: "3,499", d: "Most popular for growing businesses", f: ["Everything in Starter", "Unlimited bookings", "Daily admin summaries", "Customer reminders", "Payment collection", "5 service categories"], popular: true },
            { p: "Premium", a: "5,999", d: "For multi-location and high volume", f: ["Everything in Standard", "Multiple locations", "Custom bot flows", "Analytics dashboard", "Priority support", "White-label option"] },
          ].map(plan => (
            <div key={plan.p} className={`p-8 rounded-[2rem] flex flex-col transition-shadow relative ${plan.popular ? "bg-primary-container text-white shadow-2xl md:scale-105" : "bg-surface-container-lowest border border-outline-variant/15 hover:shadow-xl"}`}>
              {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary-container text-on-secondary-container text-[11px] font-bold tracking-wider uppercase px-4 py-1 rounded-full">Most Popular</div>}
              <div className={`text-sm font-label font-bold tracking-wider uppercase mb-5 ${plan.popular ? "text-on-primary-container" : "text-on-surface-variant"}`}>{plan.p}</div>
              <div className={`text-5xl font-headline font-extrabold mb-2 ${plan.popular ? "text-white" : "text-on-primary-fixed"}`}>
                <sup className="text-xl align-top">₹</sup>{plan.a}<span className={`text-base font-normal ml-1 ${plan.popular ? "text-on-primary-container" : "text-on-surface-variant"}`}>/mo</span>
              </div>
              <p className={`text-sm mb-8 ${plan.popular ? "text-on-primary-container" : "text-on-surface-variant"}`}>{plan.d}</p>
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {plan.f.map(f => (
                  <li key={f} className={`flex items-center gap-3 text-sm ${plan.popular ? "text-primary-fixed-dim" : "text-on-surface-variant"}`}>
                    <span className="material-symbols-outlined text-secondary text-base" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/contact")} className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${plan.popular ? "bg-secondary-container text-on-secondary-container hover:bg-secondary-fixed" : "border border-outline-variant/30 text-on-surface hover:border-secondary/40 hover:text-secondary"}`}>
                {plan.popular ? "Get Started →" : "Choose Plan"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section id="faq" className="py-32 bg-surface-container-low">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">FAQ</span>
          <h2 className="text-4xl font-headline font-bold text-primary-container mb-4">Questions answered</h2>
          <p className="text-on-surface-variant">Everything you need to know before getting started.</p>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.map((f, i) => (
            <div key={i} className={`bg-surface-container-lowest rounded-2xl border overflow-hidden transition-colors ${faq === i ? "border-secondary/30" : "border-outline-variant/15"}`}>
              <button className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 font-body text-[15px] font-medium hover:text-secondary transition-colors" onClick={() => setFaq(faq === i ? null : i)}>
                {f.q}
                <span className={`material-symbols-outlined text-xl flex-shrink-0 transition-transform ${faq === i ? "rotate-45 text-secondary" : "text-on-surface-variant"}`}>add</span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${faq === i ? "max-h-40 px-6 pb-5" : "max-h-0"}`}>
                <p className="text-sm text-on-surface-variant leading-relaxed">{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 px-6">
      <div className="max-w-5xl mx-auto bg-gradient-to-br from-primary-container to-slate-900 rounded-[3rem] p-12 lg:p-20 relative overflow-hidden text-center">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-secondary opacity-20 blur-[120px] rounded-full" />
        <div className="relative z-10">
          <h2 className="text-4xl lg:text-5xl font-headline font-bold text-white mb-6 leading-tight">
            Ready to automate your <span className="text-secondary-container">bookings?</span>
          </h2>
          <p className="text-on-primary-container text-lg mb-12 max-w-xl mx-auto">
            Join businesses across India who've switched from manual WhatsApp bookings to Bookwello. Setup takes less than 48 hours.
          </p>
          <div className="flex flex-col items-center gap-6">
            <button onClick={() => navigate("/contact")} className="bg-secondary-container text-on-secondary-container px-10 py-5 rounded-xl font-bold text-xl hover:bg-secondary-fixed transition-all shadow-lg hover:scale-105 duration-200">
              Start Your Free Trial
            </button>
            <p className="text-on-primary-container text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
              No credit card required
            </p>
          </div>
        </div>
      </div>
    </section>
  </>);
}

/* ─── Onboarding Page with Facebook Signup ─── */
function OnboardingPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", type: "", address: "" });
  const [fbUser, setFbUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleFBLogin = () => {
    setFbLoading(true);
    if (typeof window.FB === "undefined") {
      setTimeout(handleFBLogin, 1000);
      return;
    }
    window.FB.login((response) => {
      setFbLoading(false);
      if (response.authResponse) {
        window.FB.api("/me?fields=name,email,picture", (user) => {
          if (user) {
            setFbUser(user);
            setForm(f => ({
              ...f,
              name: f.name || user.name || "",
              email: f.email || user.email || ""
            }));
          }
        });
      }
    });
  };

  const submit = e => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1500);
  };

  return (
    <div className="min-h-screen bg-surface pt-24 pb-16 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">Get Started</span>
          <h1 className="text-4xl lg:text-5xl font-headline font-extrabold text-on-primary-fixed mb-4">Create your account</h1>
          <p className="text-on-surface-variant text-lg">Sign up with Facebook to pre-fill your business details</p>
        </div>

        {submitted ? (
          <div className="bg-secondary-container/20 border border-secondary/20 rounded-2xl p-10 text-center">
            <div className="text-4xl mb-3">🎉</div>
            <h3 className="text-2xl font-headline font-bold mb-2">Welcome to Bookwello!</h3>
            <p className="text-sm text-on-surface-variant mb-4">Your account has been created. We'll be in touch within 4 hours to set up your WhatsApp bot.</p>
            <p className="text-sm text-on-surface-variant">Confirmation sent to: <strong>{form.email}</strong></p>
          </div>
        ) : (
          <div className="bg-surface-container-lowest border border-outline-variant/15 rounded-[2rem] p-8">
            {!fbUser ? (
              <div className="text-center mb-6">
                <p className="text-on-surface-variant mb-6">Connect with Facebook to skip entering your details manually</p>
                <button
                  type="button"
                  onClick={handleFBLogin}
                  disabled={fbLoading}
                  className="bg-[#1877F2] text-white px-8 py-3 rounded-xl font-bold text-sm hover:bg-[#1865d6] transition-all flex items-center justify-center gap-3 mx-auto disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                  {fbLoading ? "Connecting..." : "Continue with Facebook"}
                </button>
                <div className="mt-6 pt-6 border-t border-outline-variant/15">
                  <p className="text-xs text-on-surface-variant">Or skip and enter manually below</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4 mb-6 bg-secondary-container/20 rounded-xl p-4">
                {fbUser.picture && (
                  <img src={fbUser.picture.data.url} alt="Profile" className="w-12 h-12 rounded-full" />
                )}
                <div>
                  <p className="font-bold text-sm text-on-surface">Signed in as {fbUser.name}</p>
                  <p className="text-xs text-on-surface-variant">Business info pre-filled from your profile</p>
                </div>
                <button type="button" onClick={() => setFbUser(null)} className="ml-auto text-xs text-secondary hover:underline">Change</button>
              </div>
            )}

            <form className="flex flex-col gap-4" onSubmit={submit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-on-surface-variant tracking-wide">Your Name *</label>
                  <input name="name" value={form.name} onChange={handle} placeholder="John Doe" required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-on-surface-variant tracking-wide">Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handle} placeholder="you@business.com" required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Phone Number *</label>
                <input name="phone" type="tel" value={form.phone} onChange={handle} placeholder="+91 98765 43210" required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Business Name *</label>
                <input name="business" value={form.business} onChange={handle} placeholder="Serenity Wellness Centre" required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Business Type *</label>
                <select name="type" value={form.type} onChange={handle} required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all">
                  <option value="">Select your business type</option>
                  {["Wellness Centre", "Yoga Studio", "Spa / Salon", "Dental Clinic", "Coaching / Consulting", "Gym / Fitness", "Other"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Business Address</label>
                <input name="address" value={form.address} onChange={handle} placeholder="123 Main Street, City, State" className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
              </div>
              <button type="submit" disabled={loading} className="bg-secondary text-white py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-secondary/20 mt-2">
                {loading ? "Creating Account..." : "Create Account →"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Contact Page ─── */
function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", business: "", type: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1500); };

  return (<>
    <div className="bg-surface-container-low pt-32 pb-16 border-b border-outline-variant/15 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/5 to-transparent pointer-events-none" />
      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">Get In Touch</span>
        <h1 className="text-4xl lg:text-5xl font-headline font-extrabold text-on-primary-fixed mb-4">Let's build something together</h1>
        <p className="text-on-surface-variant text-lg">Tell us about your business and we'll show you how Bookwello can automate your bookings in 48 hours.</p>
      </div>
    </div>
    <div className="py-16 px-6">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div>
          <h3 className="text-2xl font-headline font-bold mb-3">We'd love to hear from you</h3>
          <p className="text-on-surface-variant mb-8 leading-relaxed">Whether you're ready to get started or just exploring, our team responds within a few hours.</p>
          <div className="flex flex-col gap-4">
            {[
              { icon: "mail", label: "Email", val: <a href="mailto:admin@bookwello.in" className="text-secondary hover:underline">admin@bookwello.in</a> },
              { icon: "chat", label: "WhatsApp", val: <a href="https://wa.me/8824994138" className="text-secondary hover:underline">Chat with us</a> },
              { icon: "location_on", label: "Location", val: "Jaipur, Rajasthan, India" },
              { icon: "schedule", label: "Response Time", val: "Within 4 business hours" },
            ].map(c => (
              <div key={c.label} className="flex items-start gap-4 bg-surface-container-lowest border border-outline-variant/15 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-xl bg-secondary-container/50 flex items-center justify-center text-secondary flex-shrink-0">
                  <span className="material-symbols-outlined">{c.icon}</span>
                </div>
                <div>
                  <div className="text-[11px] text-on-surface-variant uppercase tracking-wider font-bold mb-1">{c.label}</div>
                  <div className="text-sm font-medium text-on-surface">{c.val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          {submitted ? (
            <div className="bg-secondary-container/20 border border-secondary/20 rounded-2xl p-10 text-center">
              <div className="text-4xl mb-3">🎉</div>
              <h3 className="text-2xl font-headline font-bold mb-2">Message received!</h3>
              <p className="text-sm text-on-surface-variant">Thanks for reaching out. We'll get back to you at <strong className="text-on-surface">{form.email}</strong> within a few hours.</p>
            </div>
          ) : (
            <form className="flex flex-col gap-4" onSubmit={submit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-on-surface-variant tracking-wide">Your Name *</label>
                  <input name="name" value={form.name} onChange={handle} placeholder="John Doe" required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-on-surface-variant tracking-wide">Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handle} placeholder="you@business.com" required className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Business Name</label>
                <input name="business" value={form.business} onChange={handle} placeholder="Serenity Wellness Centre" className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Business Type</label>
                <select name="type" value={form.type} onChange={handle} className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all">
                  <option value="">Select your business type</option>
                  {["Wellness Centre", "Yoga Studio", "Spa / Salon", "Dental Clinic", "Coaching / Consulting", "Gym / Fitness", "Other"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-on-surface-variant tracking-wide">Message *</label>
                <textarea name="message" value={form.message} onChange={handle} placeholder="Tell us about your business..." required rows={5} className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-secondary focus:ring-2 focus:ring-secondary/10 outline-none transition-all resize-none" />
              </div>
              <button type="submit" disabled={loading} className="bg-secondary text-white py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-secondary/20">
                {loading ? "Sending..." : "Send Message →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  </>);
}

/* ─── App ─── */
export default function App() {
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/onboarding" element={<EmbeddedSignup />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
      </Routes>
      <Footer />
    </>
  );
}